import AlwaysRefreshStrategy from "../refreshing/AlwaysRefreshStrategy";
import GroupInViewportRefreshStrategy from "../refreshing/GroupInViewportRefreshStrategy";
import Visibility from "../viewability/Visibility";
import fireEREvt from "../util/eventRecorderClient";
import FallbackAd from "../taglessAd/FallbackAd";
import GPT from "../gpt/GPT";
import TaglessAd from "../taglessAd/TaglessAd";
import Viewability from "../viewability/Viewability";
import Ad from "../gpt/Ad";
import AlwaysRenderRenderingStrategy from "./AlwaysRenderRenderingStrategy";
import CompanionAdRenderingStrategy from "./CompanionAdRenderingStrategy";
import InstantRenderRenderingStrategy from "./InstantRenderRenderingStrategy";
import IntersectionObserverRenderingStrategy from "./IntersectionObserverRenderingStrategy";
/**
 * Responsible for rendering all ads
 */

type Strategies = {
    always: AlwaysRenderRenderingStrategy,
    parameters: InstantRenderRenderingStrategy,
    companion: CompanionAdRenderingStrategy,
    viewable: IntersectionObserverRenderingStrategy,
}

type RefreshStrategies = {
    always: AlwaysRefreshStrategy,
    parameters: AlwaysRefreshStrategy,
    companion: AlwaysRefreshStrategy,
    viewable: GroupInViewportRefreshStrategy,
}

export default class Renderer {

    _gpt;
    _gamTaglessAd;
    _isTaglessAdEnabled;
    _showFallbackAds;
    _fallbackAd;
    _viewability;
    _visibility: Visibility;
    _strategies: Strategies | null;
    _refreshStrategies: RefreshStrategies | null;

    constructor(
        gpt: GPT,
        gamTaglessAd: TaglessAd,
        isTaglessAdEnabled: boolean,
        viewability: Viewability,
        visibility: Visibility,
        strategies = null,
        observerOptions: IntersectionObserverInit | undefined
    ) {
        this._gpt = gpt;
        this._gamTaglessAd = gamTaglessAd;
        this._isTaglessAdEnabled = isTaglessAdEnabled;
        this._showFallbackAds = false;
        this._fallbackAd = new FallbackAd();
        this._viewability = viewability;
        this._visibility = visibility;
        this._strategies = strategies;
        this._refreshStrategies = strategies;

        this._contextifyCallbacks();
        this._initStrategies(observerOptions);
        this._delegateEvents();

        if (this._visibility?.isVisible()) {
            this._start();
        }
    }

    /**
     * Binds various callback methods to `this`
     *
     * @private
     */
    private _contextifyCallbacks(): void {
        this._start = this._start.bind(this);
        this._stop = this._stop.bind(this);
    }

    /**
     * Delegates events
     *
     * @private
     */
    private _delegateEvents(): void {
        this._visibility.addEventListener(Visibility.EVENTS.ON_VISIBLE, this._start);
        this._visibility.addEventListener(Visibility.EVENTS.ON_INVISIBLE, this._stop);
    }

    /**
     * Initializes rendering strategies
     * @private
     */
    private _initStrategies(observerOptions?: IntersectionObserverInit): void {

        const strategies: Strategies = {
            always: new AlwaysRenderRenderingStrategy(
                this._gpt,
                this._gamTaglessAd,
                this._isTaglessAdEnabled,
                this._fallbackAd,
                this._showFallbackAds
            ),
            parameters: new InstantRenderRenderingStrategy(this._gpt),
            companion: new CompanionAdRenderingStrategy(this._gpt),
            viewable: new IntersectionObserverRenderingStrategy(
                this._gpt,
                this._gamTaglessAd,
                this._isTaglessAdEnabled,
                this._fallbackAd,
                this._showFallbackAds,
                observerOptions
            )
        };

        this._strategies = this._strategies || strategies;

        const refreshStrategies = {
            always: new AlwaysRefreshStrategy(),
            companion: new AlwaysRefreshStrategy(),
            parameters: new AlwaysRefreshStrategy(),
            viewable: new GroupInViewportRefreshStrategy(this._viewability)
        };

        this._refreshStrategies = this._refreshStrategies || refreshStrategies;
    }

    /**
     * Starts startable rendering strategies
     *
     */
    private _start(): void {
        this._strategies?.viewable.start();
    }

    /**
     * Stops stoppable rendering strategies
     *
     */
    private _stop(): void {
        this._strategies?.viewable.stop();
    }

    /**
     * Undelegates events
     */
    private _undelegateEvents(): void {
        this._visibility.removeEventListener(Visibility.EVENTS.ON_VISIBLE, this._start);
        this._visibility.removeEventListener(Visibility.EVENTS.ON_INVISIBLE, this._stop);
    }

    /**
     * Destructor
     */
    destroy(): void {
        this._undelegateEvents();
    }

    _getStrategy = (ad: Ad, strategy: string) => {
        if (!this._strategies) return null;

        const key = this._gpt.getEnableLazyLoad() && ad.isDecoupled() ? "always" : strategy;

        return this._strategies[key as keyof Strategies];
    }

    /**
     * Renders an ad with the given rendering strategy
     *
     * @param {Ad} ad
     * @param {object} config
     * @param {string} [config.strategy="viewable"]
     */
    render(ad: Ad, { strategy = "viewable" }): void {
        const renderingStrategy = this._getStrategy(ad, strategy);

        if (!renderingStrategy) {
            throw new Error("Sparkle: Invalid rendering strategy, supports: ['always', 'companion', 'parameters', 'viewable']");
        }

        renderingStrategy.render(ad);
    }

    private _areAdsToRefresh(adsToRefresh: Ad[] | boolean): adsToRefresh is Ad[] {
        return Array.isArray(adsToRefresh);
    }

    /**
     * Refreshes a list of ads with the given rendering and refresh strategy
     *
     * @param {Ad[]} ads
     * @param {Object} config
     * @param {string} [config.strategy="viewable"]
     */
    refresh(ads: Ad[], { strategy = "viewable" as keyof Strategies & keyof RefreshStrategies }): void {

        if (!this._strategies || !this._refreshStrategies) {
            throw new Error("Sparkle: Rendering strategies are not defined.");

        }
        const renderingStrategy = this._strategies[strategy];
        const refreshStrategy = this._refreshStrategies[strategy];

        if (!renderingStrategy) {
            throw new Error("Sparkle: Invalid rendering strategy, supports: ['always', 'companion', 'parameters', 'viewable']");
        }

        // check if an ad is part of a companion group,
        // if it is, only call refresh if all companions
        // would refresh based on their render strategy
        const adsToRefresh = refreshStrategy.wouldRefresh(ads);

        interface AdsMap {
            [key: string]: number | {
                [key: string]: string | null,
            };
        }

        if (!adsToRefresh) return;

        const msg: AdsMap = { "num-ads": ads.length };

        ads.forEach((ad) => {
            msg[ad.getId()] = {
                "dom-id": ad.getDomId(),
                "container-id": ad.getContainerId()
            };
        });

        fireEREvt("ad-refresh", msg);

        if (this._areAdsToRefresh(adsToRefresh)) {
            renderingStrategy.refresh(adsToRefresh);
        }

    }

    setFallbackFlag(flag: boolean): void {
        this._showFallbackAds = flag;
        this._strategies?.viewable?.setFallbackFlag(flag);
        this._strategies?.always?.setFallbackFlag(flag);
    }

}
